import { createIsomorphicFn } from "@tanstack/react-start";
import axios from "axios";

export function contentFailureStatus(error: unknown, fallback = 500) {
	if (!axios.isAxiosError(error)) return fallback;
	const status = error.response?.status ?? error.status;
	return status && status >= 400 && status <= 599
		? status
		: error.response
			? fallback
			: 502;
}

export function contentRetryAfter(error: unknown): string | undefined {
	if (!axios.isAxiosError(error) || error.response?.status !== 503) return;
	const value = error.response.headers["retry-after"];
	if (typeof value !== "string") return;
	if (/^\d{1,8}$/.test(value)) return value;
	if (
		value.length <= 64 &&
		!/[\r\n]/.test(value) &&
		Number.isFinite(Date.parse(value))
	)
		return new Date(value).toUTCString();
}

export const setContentResponseStatus = createIsomorphicFn()
	.server(async (status: number, retryAfter?: string) => {
		const { setResponseStatus, setResponseHeader, getResponseStatus } =
			await import("@tanstack/react-start/server");
		// A missing child must not hide a concurrent parent-loader/server failure.
		if (getResponseStatus() < 500 || status >= 500) setResponseStatus(status);
		setResponseHeader("X-Robots-Tag", "noindex, nofollow");
		setResponseHeader("Cache-Control", "no-store");
		if (status === 503 && retryAfter)
			setResponseHeader("Retry-After", retryAfter);
	})
	.client((_status: number) => {});

/** Keep SSR's error UI while applying the request-local content failure status. */
export function withContentErrorStatus(
	response: Response,
	status: number,
	requestHeaders?: {
		get(name: "Cache-Control" | "X-Robots-Tag" | "Retry-After"): unknown;
	},
) {
	if (
		status < 400 ||
		status > 599 ||
		(response.status !== 200 && response.status < 400)
	)
		return response;
	const headers = new Headers(response.headers);
	for (const name of ["Cache-Control", "X-Robots-Tag"] as const) {
		const value = requestHeaders?.get(name);
		if (value) headers.set(name, String(value));
	}
	if (status === 503) {
		const retryAfter = requestHeaders?.get("Retry-After");
		if (retryAfter) headers.set("Retry-After", String(retryAfter));
	}
	// Start merges request-local Set-Cookie headers after middleware completes.
	return new Response(response.body, {
		status,
		headers,
	});
}
